
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import * as Sentry from '@sentry/react';

const Confirmation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'password_required'>('loading');
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  const [userEmail, setUserEmail] = useState("");

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Handle the auth callback
        const { data, error } = await supabase.auth.getSession();

        if (error) {
          Sentry.captureException(error);
          setStatus('error');
          return;
        }

        if (data.session?.user) {
          const user = data.session.user;
          setUserEmail(user.email || "");

          // Check if this is a magic link login or user needs password
          // Magic link users typically don't have a password set
          const needsPassword = user.user_metadata?.invited_at || 
                               !user.user_metadata?.password_set ||
                               searchParams.get('type') === 'magiclink';
          
          if (needsPassword) {
            setStatus('password_required');
          } else {
            setStatus('success');
            toast.success("Successfully logged in!");
            navigate("/dashboard");
          }
        } else {
          // Try to exchange any tokens in the URL
          const accessToken = searchParams.get('access_token');
          const refreshToken = searchParams.get('refresh_token');

          if (accessToken && refreshToken) {
            const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });

            if (sessionError) {
              Sentry.captureException(sessionError);
              setStatus('error');
            } else if (sessionData.user) {
              setUserEmail(sessionData.user.email || "");
              setStatus('password_required');
            } else {
              setStatus('error');
            }
          } else {
            setStatus('error');
          }
        }
      } catch (error) {
        Sentry.captureException(error);
        setStatus('error');
      }
    };

    handleAuthCallback();
  }, [searchParams, navigate]);

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }

    setIsSettingPassword(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        toast.error(`Failed to set password: ${error.message}`);
      } else {
        toast.success("Password set successfully! Welcome!");
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error("An unexpected error occurred");
    } finally {
      setIsSettingPassword(false);
    }
  };

  const handleGoToLogin = () => {
    navigate("/login");
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-glyco-purple-light">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-glyco-purple mx-auto mb-4" />
          <p className="text-gray-600">Confirming your invitation...</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-glyco-purple-light">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
            <CardTitle>Welcome!</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-gray-600">
              Your account has been confirmed successfully.
            </p>
            <Button
              onClick={() => navigate("/dashboard")}
              className="w-full bg-glyco-purple hover:bg-glyco-purple-dark"
            >
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'password_required') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-glyco-purple-light p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Set Your Password</CardTitle>
            <p className="text-sm text-gray-600 mt-2">
              Welcome! Please set a password for your account: {userEmail}
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-glyco-purple hover:bg-glyco-purple-dark"
                disabled={isSettingPassword}
              >
                {isSettingPassword ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Setting Password...
                  </>
                ) : (
                  "Set Password & Continue"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-glyco-purple-light">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <CardTitle>Confirmation Failed</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-gray-600">
            There was an issue confirming your invitation. The link may have expired or is invalid.
          </p>
          <div className="space-y-2">
            <Button
              onClick={handleGoToLogin}
              className="w-full bg-glyco-purple hover:bg-glyco-purple-dark"
            >
              Go to Login
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/")}
              className="w-full"
            >
              Back to Home
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Confirmation;
